import { websiteBuilderGetSectionVisibility } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderSection";
import { faChevronDown, faChevronRight, faEllipsisVertical, faEyeSlash, IconDefinition } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { isWebsiteBuilderElementParentNode, isWebsiteBuilderParentNode, TWebsiteBuilderNode } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderNode";
import React, { ReactNode, useState } from "react";
import classNames from "classnames";
import "./layersNode.scss";
import { INavigationDropdownOption, INavigationDropdownOptionSections, NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useWebsiteBuilderElementOperations } from "pages/secure/facility/websiteBuilder/editor/hooks/nodeOperations/elementOperations/useWebsiteBuilderElementOperations";
import { useWebsiteBuilderContainerOperations } from "pages/secure/facility/websiteBuilder/editor/hooks/nodeOperations/containerOperations/useWebsiteBuilderContainerOperations";
import { useWebsiteBuilderSectionOperations } from "pages/secure/facility/websiteBuilder/editor/hooks/nodeOperations/sectionOperations/useWebsiteBuilderSectionOperations";
import { useWebsiteBuilderClipboard } from "pages/secure/facility/websiteBuilder/editor/hooks/clipboard/useWebsiteBuilderClipboard";
import { isWebsiteBuilderHideableNode } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderHideableNode";
import { websiteBuilderSetHideableNodeVisibility } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";

interface ILayersNodeProps {
  node: TWebsiteBuilderNode;
  parentNode: TWebsiteBuilderNode;
  label: string;
  icon: IconDefinition;
  depth: number;
  ancestorsVisible: boolean;
  selected: boolean;
  handleSelect?: () => void;
  children: ReactNode;
}

export function LayersNode(props: ILayersNodeProps) {
  const { node, parentNode, depth, label, icon, ancestorsVisible, selected, handleSelect, children } = props;

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);

  const clipboard = useWebsiteBuilderClipboard();

  const elementOperations = useWebsiteBuilderElementOperations(clipboard);
  const containerOperations = useWebsiteBuilderContainerOperations(clipboard);
  const sectionOperations = useWebsiteBuilderSectionOperations(clipboard);
  
  const [expanded, setExpanded] = useState<boolean>(true);
  const [hovered, setHovered] = useState<boolean>(false);

  const hasChildren = children != null && isWebsiteBuilderParentNode(node);
  const canContract = node.type !== "root" && isWebsiteBuilderParentNode(node);

  const showToggleExpanded = hasChildren && canContract;
  const showOptions = (
    node.type === "section" ||
    node.type === "container" ||
    node.type === "element"
  ) && hovered;

  function getVisible() {
    if (node.type === "section") {
      return parentNode?.type === "root"
        ? websiteBuilderGetSectionVisibility(node, parentNode)[deviceView]
        : false;
    } else if (node.type === "container") {
      return node.container.visibility[deviceView];
    } else if (node.type === "element") {
      return node.element.visibility[deviceView];
    } else {
      return true;
    }
  }

  const visible = getVisible();

  const selectable = handleSelect != null && visible && ancestorsVisible;

  function handleToggleVisibility(node: TWebsiteBuilderNode, parentNode: TWebsiteBuilderNode) {
    if (isWebsiteBuilderHideableNode(node)) {
      const updatedVisible = !visible;

      const scrollNodeId = updatedVisible ? node.id : parentNode.id;
      const inverseScrollNodeId = updatedVisible ? parentNode.id : node.id;

      dispatch(websiteBuilderSetHideableNodeVisibility(activePageId, deviceView, scrollNodeId, {
        id: node.id,
        inverseScrollNodeId,
        visible: updatedVisible,
      }));
    }
  }

  function getVisibilityOption(node: TWebsiteBuilderNode, parentNode: TWebsiteBuilderNode): INavigationDropdownOption {
    const disabled = !isWebsiteBuilderHideableNode(node);

    return {
      label: visible ? t("Hide") : t("Reveal"),
      icon: visible ? "eye-slash" : "eye",
      onClick: () => handleToggleVisibility(node, parentNode),
      disabled,
    };
  }

  function getDeleteOption(node: TWebsiteBuilderNode, onClick: () => void): INavigationDropdownOption {
    const disabled =
      (node.type === "section" && (node.section.type === "header" || node.section.type === "footer")) ||
      node.type === "grid-cell" ||
      node.type === "tab";
    
    return {
      label: t("Delete"),
      icon: "trash",
      onClick,
      disabled,
      color: !disabled ? "red" : null,
    };
  }

  function getOptionSections(): INavigationDropdownOptionSections {
    if (node.type === "section" && parentNode.type === "root") {
      return [[
        getVisibilityOption(node, parentNode),
        getDeleteOption(node, () => sectionOperations.deleteSection(node, parentNode, activePageId, deviceView)),
      ]];
    } else if (node.type === "container" && parentNode.type === "section") {
      return [[
        getVisibilityOption(node, parentNode),
        getDeleteOption(node, () => containerOperations.deleteContainer(node, parentNode, activePageId, deviceView)),
      ]];
    } else if (node.type === "element" && isWebsiteBuilderElementParentNode(parentNode)) {
      return [[
        getVisibilityOption(node, parentNode),
        getDeleteOption(node, () => elementOperations.deleteElement(node, parentNode, activePageId, deviceView)),
      ]];
    } else {
      return [];
    }
  }

  const optionSections = getOptionSections();

  function getHiddenIcon(): JSX.Element {
    return !visible ? (
      <FontAwesomeIcon
        className="layers-node-icon-action"
        icon={faEyeSlash}
        onClick={e => {
          e.stopPropagation();

          handleToggleVisibility(node, parentNode);
        }}
      />
    ) : null;
  }

  const hiddenIcon = getHiddenIcon();

  function handleContentClick() {
    if (selectable) {
      handleSelect();
    }
  }

  function handleToggleExpanded() {
    setExpanded(prev => !prev);
  }

  return (
    <div className="website-builder-layers-node">
      <div
        className={classNames("layers-node-content", {
          "layers-node-content-selectable": selectable,
          "layers-node-content-selected": selected,
        })}
        style={{
          "--layers-node-content-depth": depth,
        } as React.CSSProperties}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={handleContentClick}
      >
        <div className="layers-node-primary">
          {showToggleExpanded && (
            <FontAwesomeIcon
              icon={expanded ? faChevronDown : faChevronRight}
              className="layers-node-icon-toggle"
              onClick={e => {
                e.stopPropagation();

                handleToggleExpanded();
              }}
            />
          )}
          <FontAwesomeIcon className="layers-node-icon" icon={icon} />
          <div className="layers-node-label">{label}</div>
        </div>
        <div className="layers-node-secondary">
          {hiddenIcon}
          <div className="layers-node-dropdown-container">
            {showOptions && (
              <NavigationDropdownNew
                hideChevron
                showPlainTextLabel
                label={<FontAwesomeIcon icon={faEllipsisVertical} />}
                rightAlign
                sections={optionSections}
              />
            )}
          </div>
        </div>
      </div>

      {/* Keep children mounted and hidden while collapsed to preserve their state. */}
      {hasChildren && (
        <div style={{ display: expanded ? "contents" : "none" }}>
          {children}
        </div>
      )}
    </div>
  );
}